/**
 * @file mainScrollContext.ts
 * @brief The main-scroll context object and its `useMainScroll` consumer hook.
 *
 * @details
 * Split out of `MainScrollContext.tsx` (02-08-2026). The provider is a
 * component; a module that exports **both** a component and a value (a hook is
 * a value) loses React Fast Refresh — `@vitejs/plugin-react` can no longer
 * hot-swap it, so every edit to the provider full-reloaded the page. Context +
 * hook live here, the provider stays in the `.tsx`.
 *
 * Medical Assessment report chrome uses `chromeCollapsed` / `headerHeight`
 * so the format bar can occupy the shell topbar slot on scroll-down
 * (08-09-2026).
 *
 * @references
 * - shared/scroll/MainScrollContext.tsx
 *
 * @author DES
 * @date 02-08-2026
 */

import { createContext, useContext } from "react";

export interface MainScrollContextValue {
  scrollToSection: (sectionId: string) => boolean;
  scrollToTop: () => boolean;
  isProgrammaticScrolling: () => boolean;
  /**
   * Freeze header/footer collapse without expanding chrome — in-document
   * jumps (MLR Pages) must keep the current reading line (08-09-2026).
   */
  pauseChromeCollapse: () => void;
  /** Shell topbar/footer have translated off-screen (scroll-down). */
  chromeCollapsed: boolean;
  /**
   * Height of the shell topbar that slides away on collapse (excludes the
   * pinned quick-nav row) — sticky page chrome shifts into this gap.
   */
  headerHeight: number;
  /**
   * Drop the 16px gap the shell leaves above its footer, for a page whose
   * own bottom dock should meet the footer the way the phone's dock meets
   * the screen edge (Home, 30-09-2026). Call with true on mount and false
   * on unmount.
   */
  setFlushBottom: (flush: boolean) => void;
}

export const MainScrollContext = createContext<MainScrollContextValue | null>(
  null,
);

export function useMainScroll(): MainScrollContextValue {
  const ctx = useContext(MainScrollContext);
  if (!ctx) {
    throw new Error("useMainScroll must be used within MainScrollProvider");
  }
  return ctx;
}
